import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  UserPlus, 
  Search, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye,
  Edit,
  Download,
  Share,
  Trash2,
  FileText,
  User,
  Calendar,
  AlertTriangle
} from 'lucide-react';
import { useAccessControl } from '../hooks/useAccessControl';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PermissionGridSelector, { type PermissionType, type DataType } from './PermissionGridSelector';
import DurationSelector from './DurationSelector';

interface Patient {
  id: string;
  full_name: string;
  email: string;
  phone?: string;
}

interface AccessRequestFormProps {
  className?: string;
}

const AccessRequestForm: React.FC<AccessRequestFormProps> = ({ className }) => {
  const { user, userProfile } = useAuth();
  const {
    loading,
    permissions,
    requests,
    requestAccess
  } = useAccessControl();

  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [justification, setJustification] = useState('');
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<PermissionType[]>(['read']);
  const [selectedDataTypes, setSelectedDataTypes] = useState<DataType[]>(['all']);
  const [selectedDuration, setSelectedDuration] = useState('30 days');
  const [isEmergency, setIsEmergency] = useState(false);
  const [patientSettings, setPatientSettings] = useState<any>(null);
  const [selectedCombinations, setSelectedCombinations] = useState<Array<{ permission: PermissionType; dataType: DataType }>>(
    // Marcar todas as opções de visualizar por padrão
    [
      { permission: 'read', dataType: 'medical_records' },
      { permission: 'read', dataType: 'documents' },
      { permission: 'read', dataType: 'lab_results' },
      { permission: 'read', dataType: 'prescriptions' },
      { permission: 'read', dataType: 'imaging' },
      { permission: 'read', dataType: 'personal_info' }
    ]
  );

  // Buscar pacientes
  const searchPatients = async (term: string) => {
    if (!term.trim()) {
      setPatients([]);
      return;
    }

    setLoadingPatients(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, phone')
        .eq('role', 'patient')
        .or(`full_name.ilike.%${term}%,email.ilike.%${term}%`)
        .limit(10);

      if (error) {
        console.error('Erro ao buscar pacientes:', error);
        toast.error('Erro ao buscar pacientes');
        return;
      }

      setPatients(data || []);
    } catch (error) {
      console.error('Erro ao buscar pacientes:', error);
      toast.error('Erro ao buscar pacientes');
    } finally {
      setLoadingPatients(false);
    }
  };

  // Debounce da busca
  useEffect(() => {
    const timer = setTimeout(() => {
      searchPatients(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Carregar configurações do paciente selecionado
  const loadPatientSettings = async (patientId: string) => {
    try {
      const { data, error } = await supabase
        .from('patient_access_settings')
        .select('*')
        .eq('patient_id', patientId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao carregar configurações do paciente:', error);
        return;
      }

      setPatientSettings(data);
      
      // Definir duração padrão baseada nas configurações do paciente
      if (data?.auto_approve_duration) {
        setSelectedDuration(data.auto_approve_duration);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações do paciente:', error);
    }
  };

  // Carregar configurações quando paciente for selecionado
  useEffect(() => {
    if (selectedPatient) {
      loadPatientSettings(selectedPatient.id);
    } else {
      setPatientSettings(null);
      setSelectedDuration('30 days');
    }
  }, [selectedPatient]);

  // Sincronizar seleções do grid
  const handleGridSelectionChange = (combinations: Array<{ permission: PermissionType; dataType: DataType }>) => {
    setSelectedCombinations(combinations);
    
    // Extrair permissões únicas para validação
    const permissions = [...new Set(combinations.map(c => c.permission))];
    setSelectedPermissions(permissions);
    
    // Extrair tipos de dados únicos para validação
    const dataTypes = [...new Set(combinations.map(c => c.dataType))];
    setSelectedDataTypes(dataTypes.filter(dt => dt !== 'all'));
  };

  const handleRequestAccess = async () => {
    if (!selectedPatient) {
      toast.error('Selecione um paciente');
      return;
    }

    if (selectedCombinations.length === 0) {
      toast.error('Selecione pelo menos uma combinação de permissão e tipo de dados');
      return;
    }

    if (!justification.trim()) {
      toast.error('Justificativa é obrigatória');
      return;
    }

    if (!selectedDuration) {
      toast.error('Selecione a duração do acesso');
      return;
    }

    // Usar apenas as combinações específicas selecionadas
    const permissionTypes = selectedCombinations.map(c => c.permission);
    const dataTypes = selectedCombinations.map(c => c.dataType);
    
    const result = await requestAccess(
      selectedPatient.id,
      permissionTypes,
      dataTypes,
      justification,
      selectedDuration,
      isEmergency
    );

    if (result.success) {
      toast.success('Solicitação de acesso enviada com sucesso!');
      // Reset form
      setSelectedPatient(null);
      setJustification('');
      setSearchTerm('');
      setSelectedPermissions(['read']);
      setSelectedDataTypes(['all']);
      setSelectedDuration('30 days');
      setIsEmergency(false);
      setPatientSettings(null);
    } else {
      toast.error(result.error || 'Erro ao enviar solicitação');
    }
  };

  const getPermissionIcon = (type: string) => {
    switch (type) {
      case 'read': return <Eye className="h-4 w-4" />;
      case 'write': return <Edit className="h-4 w-4" />;
      case 'download': return <Download className="h-4 w-4" />;
      case 'share': return <Share className="h-4 w-4" />;
      case 'delete': return <Trash2 className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pendente</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Aprovado</Badge>;
      case 'denied':
        return <Badge className="bg-red-100 text-red-800">Negado</Badge>;
      case 'revoked':
        return <Badge className="bg-gray-100 text-gray-800">Revogado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getPermissionTypeLabel = (type: string) => {
    switch (type) {
      case 'read': return 'Visualizar';
      case 'write': return 'Editar';
      case 'download': return 'Baixar';
      case 'share': return 'Compartilhar';
      case 'delete': return 'Excluir';
      default: return type;
    }
  };

  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const myRequests = requests.filter(r => (r as any).requester_id === user?.id);
    const myPermissions = permissions.filter(p => (p as any).requester_id === user?.id);
  const activePermissions = myPermissions.filter(p => 
    p.status === 'approved' && !isExpired(p.expires_at)
  );

  if (userProfile?.role === 'patient') {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Esta funcionalidade está disponível apenas para profissionais de saúde.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Solicitações de Acesso</h2>
          <p className="text-gray-600">Solicite acesso aos dados médicos dos pacientes</p>
        </div>
      </div>

      {/* Formulário Simplificado */}
      <Card>
        <CardHeader>
          <CardTitle>Nova Solicitação de Acesso</CardTitle>
          <CardDescription>
            Solicite acesso aos dados médicos de um paciente
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Buscar Paciente</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Nome ou email do paciente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {loadingPatients && (
              <p className="text-sm text-gray-500">Buscando pacientes...</p>
            )}
            {patients.length > 0 && (
              <div className="border rounded-md max-h-40 overflow-y-auto">
                {patients.map((patient) => (
                  <div
                    key={patient.id}
                    className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                      selectedPatient?.id === patient.id ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                    onClick={() => {
                      setSelectedPatient(patient);
                      setSearchTerm(patient.full_name);
                      setPatients([]);
                    }}
                  >
                    <div className="font-medium">{patient.full_name}</div>
                    <div className="text-sm text-gray-500">{patient.email}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedPatient && (
            <Alert>
              <User className="h-4 w-4" />
              <AlertDescription>
                Paciente selecionado: <strong>{selectedPatient.full_name}</strong>
              </AlertDescription>
            </Alert>
          )}

          {selectedPatient && (
            <>
              {/* Seleção de Permissões - Matriz Visual */}
              <PermissionGridSelector
                selectedCombinations={selectedCombinations}
                onSelectionChange={handleGridSelectionChange}
              />

              {/* Seleção de Duração */}
              <DurationSelector
                selectedDuration={selectedDuration}
                onDurationChange={setSelectedDuration}
                defaultDuration={patientSettings?.auto_approve_duration || '30 days'}
                patientSettings={patientSettings}
                isEmergency={isEmergency}
                onEmergencyChange={setIsEmergency}
              />

              {/* Justificativa */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Justificativa *</label>
                <textarea
                  placeholder="Explique por que você precisa deste acesso..."
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500">
                  {patientSettings?.require_justification 
                    ? 'Este paciente exige justificativa detalhada para todas as solicitações.'
                    : 'Forneça uma justificativa clara para sua solicitação de acesso.'
                  }
                </p>
              </div>

              <Button 
                onClick={handleRequestAccess}
                disabled={loading || !selectedPatient || !justification.trim() || selectedCombinations.length === 0}
                className="w-full"
                size="lg"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {isEmergency ? 'Enviar Solicitação de Emergência' : 'Enviar Solicitação'}
              </Button>
            </>
          )}

          {!selectedPatient && (
            <Alert>
              <User className="h-4 w-4" />
              <AlertDescription>
                Selecione um paciente para configurar os detalhes da solicitação de acesso.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Solicitações Pendentes</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{myRequests.filter(r => r.status === 'pending').length}</div>
            <p className="text-xs text-muted-foreground">
              Aguardando aprovação
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Acessos Ativos</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activePermissions.length}</div>
            <p className="text-xs text-muted-foreground">
              Permissões aprovadas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Solicitações</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{myRequests.length}</div>
            <p className="text-xs text-muted-foreground">
              Todas as solicitações
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Minhas Solicitações</h3>
        {myRequests.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500 text-center">
                Nenhuma solicitação encontrada
              </p>
              <p className="text-sm text-gray-400 text-center mt-2">
                Use o formulário acima para fazer uma nova solicitação
              </p>
            </CardContent>
          </Card>
        ) : (
          myRequests.map((request) => (
            <Card key={request.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getPermissionIcon(request.permission_type)}
                    <div>
                      <CardTitle className="text-lg">
                        {(request as any).patient?.full_name || 'Paciente'}
                      </CardTitle>
                      <CardDescription>
                        Acesso para {getPermissionTypeLabel(request.permission_type)}
                      </CardDescription>
                    </div>
                  </div>
                  {getStatusBadge(request.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <strong>Solicitado:</strong><br />
                      {formatDistanceToNow(new Date(request.created_at), { 
                        addSuffix: true, 
                        locale: ptBR 
                      })}
                    </div>
                    {request.expires_at && (
                      <div>
                        <strong>Expira:</strong><br />
                        {formatDistanceToNow(new Date(request.expires_at), { 
                          addSuffix: true, 
                          locale: ptBR 
                        })}
                      </div>
                    )}
                  </div>
                  {request.status === 'approved' && request.granted_at && (
                    <div className="text-sm text-green-600">
                      <strong>Aprovado:</strong> {formatDistanceToNow(new Date(request.granted_at), { 
                        addSuffix: true, 
                        locale: ptBR 
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AccessRequestForm;